import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Analytics from 'analytics';
import { EVENTS } from 'analytics/events';
import './homepage.css';

// ─────────────────────────────────────────────────────────────────
// Demo board data
// ─────────────────────────────────────────────────────────────────
const CATEGORIES = [
  'OFFICE LORE',
  'POP CULTURE 2020s',
  'BAR MATH',
  '"GAD"ZOOKS',
  'WEDDING TOASTS',
  'TINY HISTORY',
];

const VALUES = [200, 400, 600, 800, 1000];

const SAMPLE_CLUES = [
  { cat: 'POP CULTURE 2020s', value: 400, text: 'IN 2022, STRANGER THINGS SHOT THIS 1985 KATE BUSH SONG BACK TO #1 — 37 YEARS LATE.' },
  { cat: 'OFFICE LORE', value: 600, text: 'SLACK\'S NAME IS AN ACRONYM: "SEARCHABLE LOG OF ALL CONVERSATION AND" THIS.' },
  { cat: 'TINY HISTORY', value: 1000, text: 'CLEOPATRA LIVED CLOSER IN TIME TO THE MOON LANDING THAN TO THE BUILDING OF THIS WONDER OF THE WORLD.' },
  { cat: 'BAR MATH', value: 200, text: 'FOUR FRIENDS, $63 TAB, AND ONE "FORGOT" HIS WALLET. THIS IS WHAT EACH PAYER ENDS UP COVERING.' },
  { cat: '"GAD"ZOOKS', value: 800, text: 'A PERSISTENT, ANNOYING CRITIC — AND, AT HIS TRIAL, WHAT SOCRATES CALLED HIMSELF.' },
  { cat: 'WEDDING TOASTS', value: 400, text: 'THE WHITE WEDDING DRESS BECAME STANDARD AFTER THIS QUEEN WORE ONE TO HER 1840 WEDDING.' },
];

const LOGO = `${process.env.PUBLIC_URL}/g2-logo.png`;

// ─────────────────────────────────────────────────────────────────
// Hero animated mock game board + phone overlays
// ─────────────────────────────────────────────────────────────────
function HeroBoard() {
  const [gone, setGone] = useState(new Set());
  const [flash, setFlash] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const [clueIdx, setClueIdx] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    let step = 0;
    const tick = () => {
      const clue = SAMPLE_CLUES[step % SAMPLE_CLUES.length];
      const key = `${clue.cat}|${clue.value}`;
      setFlash(key);
      setTimeout(() => {
        setOverlay(clue);
        setFlash(null);
      }, 350);
      setTimeout(() => {
        setOverlay(null);
        setGone(prev => new Set(prev).add(key));
      }, 3600);
      step++;
      setClueIdx(step);
    };
    tick();
    intervalRef.current = setInterval(tick, 4400);
    return () => clearInterval(intervalRef.current);
  }, []);

  const handleTileClick = (cat, val) => {
    const key = `${cat}|${val}`;
    if (gone.has(key) || overlay) return;
    setFlash(key);
    const clue = SAMPLE_CLUES.find(c => c.cat === cat && c.value === val) || SAMPLE_CLUES[0];
    setTimeout(() => {
      setOverlay({ ...clue, cat, value: val });
      setFlash(null);
    }, 350);
    setTimeout(() => {
      setOverlay(null);
      setGone(prev => new Set(prev).add(key));
    }, 4000);
  };

  return (
    <div className="hp-hero-stage">
      <div className="hp-tv-frame">
        <div className="hp-tv-bezel-light" />
        <div className="hp-board">
          {CATEGORIES.map(c => (
            <div className="hp-cat" key={c}>{c}</div>
          ))}
          {VALUES.map(v =>
            CATEGORIES.map(c => {
              const key = `${c}|${v}`;
              const isGone = gone.has(key);
              const isFlash = flash === key;
              return (
                <div
                  key={key}
                  className={`hp-tile${isGone ? ' hp-gone' : ''}${isFlash ? ' hp-flash' : ''}`}
                  onClick={() => handleTileClick(c, v)}
                >
                  {!isGone && `$${v}`}
                </div>
              );
            })
          )}
          {overlay && (
            <div className="hp-clue-overlay" key={clueIdx}>
              <div className="hp-clue-cat">{overlay.cat} · ${overlay.value}</div>
              <div className="hp-clue-text">{overlay.text}</div>
            </div>
          )}
        </div>
      </div>

      {/* Phone 1 — player typing their answer */}
      <div className="hp-phone hp-phone-1" aria-hidden="true">
        <div className="hp-phone-screen">
          <div className="hp-phone-name">Jess</div>
          <div className="hp-phone-score">$1,800</div>
          <div className={`hp-phone-input hp-typed`}>a gadfly</div>
          <div className="hp-phone-correct">✓ CORRECT</div>
        </div>
      </div>

      {/* Phone 2 — host narration */}
      <div className="hp-phone hp-phone-2" aria-hidden="true">
        <div className="hp-phone-screen">
          <div className="hp-phone-name">Host</div>
          <div className="hp-phone-host">
            <div className="hp-h-label">🎙 Now reading</div>
            "Twenty answers in. Jess locked 'a gadfly' first — and she's right."
          </div>
          <div className="hp-phone-buzz">SUBMIT ANSWER</div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// FAQ accordion — native <details> for SEO friendliness
// ─────────────────────────────────────────────────────────────────
const FAQS = [
  {
    q: 'What is Groupardy?',
    a: <>Groupardy is an <strong>AI-powered Jeopardy-style trivia game</strong> for groups. You play on a big screen (TV, laptop, projector) while everyone else joins from their phone. An AI host reads the clues, judges answers, and keeps the energy moving — just like a real game show.</>,
  },
  {
    q: 'How many people can play?',
    a: <>Anywhere from <strong>1 to 100 players</strong> in a single game. Solo runs work great, and we've run Groupardy at house parties, all-hands meetings, weddings, classrooms, and packed bar nights. The board scales, the scoreboard scales, the chaos scales.</>,
  },
  {
    q: 'Do I need to download an app?',
    a: <>No. Groupardy runs entirely in the browser. The host opens groupardy.com on a TV or shared screen, players scan the QR code on their phones — and you're playing in under a minute.</>,
  },
  {
    q: 'Is Groupardy free?',
    a: <>Yes — Groupardy is <strong>free right now, for a limited time</strong>. There's no premium tier and no card required. We'll have to start charging in a few weeks, so this is a great moment to jump in and host a few games on us.</>,
  },
  {
    q: 'Can I customize the questions and categories?',
    a: <>Yes. Give us a topic ("early 2000s pop music", "company onboarding", "my Aunt Lori") and our AI builds a full <strong>50-clue game</strong> across two 25-question rounds, tailored to your group. The more context you give it, the better the board.</>,
  },
  {
    q: 'How does the AI host work?',
    a: <>Our host reads every clue out loud with theatrical timing, judges open-ended answers ("close enough" counts), reacts to wagers, narrates comebacks, and crowns the winner.</>,
  },
  {
    q: 'Is it good for kids, work, or sensitive groups?',
    a: <>Yes — you can describe your group however you want when you create the game ("8-year-olds at a birthday party", "a Q4 sales kickoff", "my chaotic friends"). The AI does its best to match the tone, topics, and difficulty to what you tell it.</>,
  },
  {
    q: 'Can I save games and play them again?',
    a: <>Yes — you can copy a game to play it again with a new group. The questions stay the same; the players (and the scores) reset.</>,
  },
];

function FAQ() {
  return (
    <div className="hp-faq-list">
      {FAQS.map((f, i) => (
        <details className="hp-faq-item" key={i} open={i === 0}>
          <summary>
            <span>{f.q}</span>
            <span className="hp-faq-toggle">+</span>
          </summary>
          <div className="hp-faq-body">{f.a}</div>
        </details>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// JSON-LD structured data
// ─────────────────────────────────────────────────────────────────
const LD_WEBAPP = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Groupardy',
  url: 'https://groupardy.com/',
  applicationCategory: 'GameApplication',
  applicationSubCategory: 'Trivia',
  operatingSystem: 'Any (web browser)',
  description: 'Groupardy is an AI-powered Jeopardy-style trivia game show for groups of 2 to 100 players. The board runs on a shared screen while players join from their phones. An AI host reads clues, judges answers, and narrates wagers in real time.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
  featureList: [
    'AI-generated trivia boards tailored to your group\'s topic and audience',
    'Real-time multiplayer for 1–100 players on phones',
    'AI host with theatrical narration and closed captioning',
    'Open-ended answer judging (close-enough counts)',
    'Wagering, Daily Doubles, and Final Jeopardy-style finishes',
    'Shareable recap cards and superlatives',
  ],
  aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', ratingCount: '1280' },
  publisher: { '@type': 'Organization', name: 'Groupardy', url: 'https://groupardy.com/', logo: 'https://groupardy.com/g2-logo.png' },
});

const LD_FAQ = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: typeof f.a === 'string' ? f.a : f.q },
  })),
});

const LD_ORG = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Groupardy',
  url: 'https://groupardy.com/',
  logo: 'https://groupardy.com/g2-logo.png',
  sameAs: ['https://x.com/groupardy', 'https://www.instagram.com/groupardy/'],
  description: 'Groupardy is an AI-powered Jeopardy-style trivia game show platform for groups of 2 to 100 players.',
});

const LD_HOWTO = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to host a Groupardy trivia game',
  description: 'Set up a Jeopardy-style trivia game show for your group in under two minutes.',
  totalTime: 'PT2M',
  step: [
    { '@type': 'HowToStep', position: 1, name: 'Pick a vibe', text: 'Open groupardy.com and tell the AI your group and the energy you want. Groupardy generates a custom 50-clue game across two rounds tailored to your group.' },
    { '@type': 'HowToStep', position: 2, name: 'Cast and connect', text: 'Open the board on a TV, laptop, or projector. Players scan a QR code on their phones to join.' },
    { '@type': 'HowToStep', position: 3, name: 'Press play', text: 'The AI host reads every clue, judges open-ended answers, narrates wagers, and crowns the winner with a shareable recap card.' },
  ],
});

// ─────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────
const Homepage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(window.location.search);
    const referrerDomain = document.referrer
      ? (() => { try { return new URL(document.referrer).hostname; } catch { return undefined; } })()
      : undefined;
    Analytics.track(EVENTS.HOMEPAGE_VIEWED, {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_content: params.get('utm_content') || undefined,
      referrer_domain: referrerDomain,
    });
  }, []);

  const trackCta = (location, destination) => {
    Analytics.track(EVENTS.HOMEPAGE_CTA_CLICKED, { cta_location: location, destination });
  };

  return (
    <div className="homepage">
      <Helmet>
        <title>Groupardy · AI-powered Jeopardy-style trivia for groups of 2–100</title>
        <meta name="description" content="Groupardy is the AI-powered Jeopardy-style game show for groups. Bring your TV and your phones — we'll bring the board, the questions, and a host with impeccable timing. Free to start, no download, up to 100 players." />
        <meta name="keywords" content="Groupardy, AI trivia game, group trivia, Jeopardy-style game, party trivia, team building trivia, trivia for groups, multiplayer trivia, bar trivia software, classroom trivia, custom trivia game, AI game host" />
        <meta name="author" content="Groupardy" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://groupardy.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://groupardy.com/" />
        <meta property="og:title" content="Groupardy · AI-powered trivia game show for groups" />
        <meta property="og:description" content="Host the trivia night your group will never shut up about. AI-generated boards, an AI host with real timing, up to 100 players on their phones. Free to start." />
        <meta property="og:site_name" content="Groupardy" />
        <meta property="og:image" content="https://groupardy.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Groupardy · AI-powered trivia for groups" />
        <meta name="twitter:description" content="Bring the TV. Bring the phones. We'll bring the host. Free to start, up to 100 players." />
        <meta name="twitter:image" content="https://groupardy.com/og-image.jpg" />
        <meta name="twitter:site" content="@groupardy" />
        <meta name="theme-color" content="#2ec4b6" />
        <script type="application/ld+json">{LD_WEBAPP}</script>
        <script type="application/ld+json">{LD_FAQ}</script>
        <script type="application/ld+json">{LD_ORG}</script>
        <script type="application/ld+json">{LD_HOWTO}</script>
      </Helmet>

      {/* ─── NAV ─── */}
      <header className="hp-nav">
        <div className="hp-wrap hp-nav-inner">
          <a className="hp-nav-brand" href="/" aria-label="Groupardy home">
            <img src={LOGO} alt="Groupardy logo" className="hp-nav-logo" />
            <span className="hp-nav-wordmark">Groupardy</span>
          </a>
          <nav className="hp-nav-links" aria-label="Primary">
            <a className="hp-nav-link" href="#how">How it works</a>
            <a className="hp-nav-link" href="#use-cases">Use cases</a>
            <a className="hp-nav-link" href="#host">AI host</a>
            <a className="hp-nav-link" href="#faq">FAQ</a>
          </nav>
          <div className="hp-nav-actions">
            <a className="hp-nav-signin" href="/login">Sign in</a>
            <a
              className="hp-btn hp-btn-primary hp-btn-sm"
              href="/create"
              onClick={() => trackCta('nav', '/create')}
            >
              Start a game
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* ─── HERO ─── */}
        <section className="hp-hero" aria-labelledby="hero-title">
          <div className="hp-wrap hp-hero-grid">
            <div>
              <div className="hp-hero-eyebrow">
                <span className="hp-pulse-dot" /> Live on the TV · Played on phones
              </div>
              <h1 id="hero-title">
                Host the trivia&nbsp;night<br />
                your group will <span className="hp-underlined">never shut up</span> about.
              </h1>
              <div className="hp-hero-cta-row">
                <a
                  className="hp-btn hp-btn-primary hp-btn-lg"
                  href="/create"
                  onClick={() => trackCta('hero_primary', '/create')}
                >
                  Start a game
                </a>
              </div>
              <div className="hp-hero-trust">
                <span><span className="hp-check">✓</span> No download</span>
                <span><span className="hp-check">✓</span> Free to start</span>
                <span><span className="hp-check">✓</span> Setup in 2 minutes</span>
                <span><span className="hp-check">✓</span> Up to 100 players</span>
              </div>
            </div>
            <HeroBoard />
          </div>
        </section>

        {/* ─── HOW IT WORKS ─── */}
        <section className="hp-section" id="how">
          <div className="hp-wrap">
            <div className="hp-section-head">
              <div className="hp-section-eyebrow">How it works</div>
              <h2>Three steps from "huh, what should we do tonight?" to "OK who wants to wager it all".</h2>
              <p className="hp-sub">Setup is the part of game night nobody enjoys. We made it ninety seconds.</p>
            </div>
            <div className="hp-steps">
              {/* Step 1 */}
              <div className="hp-step">
                <div className="hp-step-illus" aria-hidden="true">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, fontFamily: 'var(--font-brand)', fontSize: '0.7rem', letterSpacing: '0.16em', color: 'var(--teal-deep)' }}>
                    <span style={{ padding: '4px 10px', background: '#fff', border: '1.5px solid var(--teal)', borderRadius: 999 }}>"COMPANY ONBOARDING"</span>
                    <span style={{ padding: '4px 10px', background: '#fff', border: '1.5px dashed var(--line-strong)', borderRadius: 999, color: 'var(--ink-soft)' }}>"GEN Z SLANG"</span>
                    <span style={{ padding: '4px 10px', background: 'var(--gold)', border: '1.5px solid var(--gold)', borderRadius: 999, color: 'var(--ink)' }}>WORKPLACE · MEDIUM</span>
                  </div>
                </div>
                <div className="hp-step-num">1</div>
                <h3>Pick a vibe</h3>
                <p>Tell us your group and the energy you want. Our AI writes a custom board with categories tuned to your room.</p>
              </div>

              {/* Step 2 */}
              <div className="hp-step">
                <div className="hp-step-illus" aria-hidden="true">
                  <div className="hp-mini-tv">
                    {Array.from({ length: 25 }).map((_, i) => <div className="hp-t" key={i} />)}
                  </div>
                  <div style={{ position: 'absolute', bottom: 10, display: 'flex', gap: 6 }}>
                    <div className="hp-mini-phone"><div className="hp-ps" /></div>
                    <div className="hp-mini-phone hp-b"><div className="hp-ps" /></div>
                    <div className="hp-mini-phone hp-c"><div className="hp-ps" /></div>
                    <div className="hp-mini-phone"><div className="hp-ps" /></div>
                  </div>
                </div>
                <div className="hp-step-num">2</div>
                <h3>Cast &amp; connect</h3>
                <p>Open the board on a TV, laptop, or projector. Players scan a QR code on their phones — done.</p>
              </div>

              {/* Step 3 */}
              <div className="hp-step">
                <div className="hp-step-illus" aria-hidden="true">
                  <div className="hp-mini-host">
                    <div className="hp-mh-bar" />
                    <div className="hp-mh-bar hp-s" />
                    <div className="hp-mh-bar hp-t" />
                    <div className="hp-mh-bar hp-s" />
                    <div className="hp-mh-bar hp-t" />
                  </div>
                </div>
                <div className="hp-step-num">3</div>
                <h3>Press play</h3>
                <p>The AI host reads each clue, judges answers (close enough counts), narrates the wagers, and crowns the winner with confetti and a shareable recap.</p>
              </div>
            </div>
            <div className="hp-inline-cta">
              <a
                className="hp-btn hp-btn-primary hp-btn-lg"
                href="/create"
                onClick={() => trackCta('after_how', '/create')}
              >
                Start a game
              </a>
            </div>
          </div>
        </section>

        {/* ─── USE CASES ─── */}
        <section className="hp-section" id="use-cases" style={{ background: 'var(--cream-soft)', position: 'relative' }}>
          <div className="hp-wrap">
            <div className="hp-section-head">
              <div className="hp-section-eyebrow">Made for groups</div>
              <h2>If you've got a group, you've got a game night.</h2>
              <p className="hp-sub">Groupardy adapts its tone, difficulty, and topic mix to the crowd. Same product, very different nights.</p>
            </div>
            <div className="hp-cases hp-cases-3">
              <div className="hp-case hp-case-1">
                <div className="hp-case-icon">🛋️</div>
                <h4>Friday game night</h4>
                <p>Pop culture, hot takes, and just enough chaos.</p>
              </div>
              <a
                className="hp-case hp-case-2"
                href="/virtual-team-building-trivia"
                onClick={() => trackCta('use_case_team_building', '/virtual-team-building-trivia')}
              >
                <div className="hp-case-icon">💼</div>
                <h4>Office &amp; team building</h4>
                <p>Workplace-safe topics, company-specific categories, and team scoring. Onboarding entire departments since 2024.</p>
              </a>
              <div className="hp-case hp-case-6">
                <div className="hp-case-icon">💍</div>
                <h4>Weddings &amp; reunions</h4>
                <p>The reception activity that's actually fun. Stories about the couple, family lore, and a Daily Double nobody saw coming.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── AI HOST ─── */}
        <section className="hp-host-band" id="host">
          <div className="hp-wrap hp-host-grid">
            <div>
              <div className="hp-section-eyebrow">The AI host</div>
              <h2>You bring the snacks.<br />We bring the showmanship.</h2>
              <p className="hp-sub">
                Most trivia apps make one of your friends read the questions. They lose. Groupardy ships with a host
                who never misses a punchline and never gets a turn skipped.
              </p>
              <ul className="hp-host-list">
                <li>
                  <div className="hp-ic">🎙</div>
                  <div>
                    <strong>Reads with real timing</strong>
                    <p>Theatrical pauses on Daily Doubles. Crisp pacing on the easy stuff. Adjusts energy to the round.</p>
                  </div>
                </li>
                <li>
                  <div className="hp-ic">⚖️</div>
                  <div>
                    <strong>Judges open answers</strong>
                    <p>"Constantinople" and "Istanbul" both count. Typos forgiven. Sarcasm noticed, never rewarded.</p>
                  </div>
                </li>
                <li>
                  <div className="hp-ic">📺</div>
                  <div>
                    <strong>Closed captions, every clue</strong>
                    <p>Captions on by default. Volume off? Still playable. The board never holds anyone up.</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="hp-host-card" aria-hidden="true">
              <div className="hp-host-mic">🎙</div>
              <p className="hp-host-quote">
                "Late entry from Jess in row two — <em>and it's correct.</em>
                That puts her ahead by two hundred. Mike, you're up. Choose carefully."
              </p>
              <div className="hp-host-bars">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div className="hp-bar" key={i} />
                ))}
              </div>
              <div className="hp-host-caption">
                <span className="hp-cc">CC</span>
                <span>Closed captions · auto</span>
              </div>
              <div className="hp-scoreboard">
                <div className="hp-scoreboard-row hp-leader">
                  <div className="hp-rank">1</div>
                  <div className="hp-pname">Jess</div>
                  <div className="hp-pscore">$3,400</div>
                </div>
                <div className="hp-scoreboard-row">
                  <div className="hp-rank">2</div>
                  <div className="hp-pname">Mike</div>
                  <div className="hp-pscore">$3,200</div>
                </div>
                <div className="hp-scoreboard-row">
                  <div className="hp-rank">3</div>
                  <div className="hp-pname">Priya</div>
                  <div className="hp-pscore">$2,800</div>
                </div>
                <div className="hp-scoreboard-row">
                  <div className="hp-rank">4</div>
                  <div className="hp-pname">Dave</div>
                  <div className="hp-pscore">$1,600</div>
                </div>
              </div>
            </div>
          </div>
          <div className="hp-wrap hp-inline-cta">
            <a
              className="hp-btn hp-btn-primary hp-btn-lg"
              href="/create"
              onClick={() => trackCta('after_host', '/create')}
            >
              Start a game
            </a>
          </div>
        </section>

        {/* ─── TESTIMONIALS ─── */}
        <section className="hp-section" id="testimonials">
          <div className="hp-wrap">
            <div className="hp-section-head hp-center">
              <div className="hp-section-eyebrow" style={{ justifyContent: 'center', display: 'inline-flex' }}>People in rooms</div>
              <h2>"This became our Friday."</h2>
            </div>
            <div className="hp-testimonials">
              {[
                { initials: 'DL', name: 'Dani L.', role: 'Head of People, mid-sized SaaS', quote: "It replaced our team's standing happy hour. We've done 14 games. The \"Things Marketing Has Said In Slack\" category is now legend." },
                { initials: 'MR', name: 'Marco R.', role: 'Played at a 70th birthday', quote: "I hosted my mom's 70th with a custom board about her life. She cried twice. The Final Jeopardy was about my dad. I'd pay $200 for that." },
                { initials: 'PS', name: 'Priya S.', role: 'GM, neighborhood bar', quote: 'Our trivia bar swapped a paid host for Groupardy on Tuesdays. The crowd doubled. We now serve a $14 cocktail called "Daily Double".' },
              ].map((t, i) => (
                <div className="hp-testimonial" key={i}>
                  <blockquote>{t.quote}</blockquote>
                  <div className="hp-t-author">
                    <div className="hp-t-avatar">{t.initials}</div>
                    <div className="hp-t-meta">
                      <div className="hp-t-name">{t.name}</div>
                      <div className="hp-t-role">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FAQ ─── */}
        <section className="hp-section hp-faq-band" id="faq">
          <div className="hp-wrap">
            <div className="hp-section-head hp-center">
              <div className="hp-section-eyebrow" style={{ justifyContent: 'center', display: 'inline-flex' }}>FAQ</div>
              <h2>Questions, answered.</h2>
              <p className="hp-sub">If you have one we missed, ask in our group chat — there's literally one for that.</p>
            </div>
            <FAQ />
          </div>
        </section>

        {/* ─── FINAL CTA ─── */}
        <section className="hp-final-cta" id="cta">
          <div className="hp-wrap hp-final-cta-inner">
            <div className="hp-section-eyebrow">Final Jeopardy</div>
            <h2>This is the part where you press the button.</h2>
            <p className="hp-sub">
              Make a board, send the link, watch your group chat lose its mind. Free to start, no card,
              one minute to your first clue.
            </p>
            <div className="hp-final-cta-row">
              <a
                className="hp-btn hp-btn-gold hp-btn-lg"
                href="/create"
                onClick={() => trackCta('final_cta', '/create')}
              >
                Start a game
              </a>
            </div>
            <div className="hp-final-trust">
              <span>No credit card</span>
              <span>·</span>
              <span>No download</span>
              <span>·</span>
              <span>Up to 100 players</span>
              <span>·</span>
              <span>Free for a limited time</span>
            </div>
          </div>
        </section>
      </main>

      {/* ─── FOOTER ─── */}
      <footer className="hp-footer">
        <div className="hp-wrap">
          <div className="hp-foot-grid">
            <div className="hp-foot-brand">
              <div className="hp-wm">
                <img src={LOGO} alt="" style={{ width: 28, height: 28 }} />
                Groupardy
              </div>
              <p>The AI-powered Jeopardy-style game show for groups. A real game show for the room you're already in.</p>
              <div className="hp-foot-socials">
                <a href="https://www.instagram.com/groupardy/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">📷</a>
                <a href="https://x.com/groupardy" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">𝕏</a>
              </div>
            </div>
            <div className="hp-foot-col">
              <h5>Play</h5>
              <ul>
                <li>
                  <a href="/create" onClick={() => trackCta('footer', '/create')}>
                    Start a game
                  </a>
                </li>
              </ul>
            </div>
            <div className="hp-foot-col">
              <h5>Product</h5>
              <ul>
                <li><a href="#how">How it works</a></li>
                <li><a href="#host">The AI host</a></li>
                <li><a href="/virtual-team-building-trivia">Virtual team building</a></li>
              </ul>
            </div>
            <div className="hp-foot-col">
              <h5>Company</h5>
              <ul>
                <li><a href="/about">About</a></li>
                <li><a href="/blog">Blog</a></li>
                {/* <li><a href="/press">Press</a></li> */}
                {/* <li><a href="/contact">Contact</a></li> */}
              </ul>
            </div>
            <div className="hp-foot-col">
              <h5>Support</h5>
              <ul>
                {/* <li><a href="/help">Help center</a></li> */}
                <li><a href="#faq">FAQ</a></li>
                <li><a href="/terms">Terms</a></li>
                <li><a href="/privacy">Privacy</a></li>
              </ul>
            </div>
          </div>
          <div className="hp-foot-bar">
            <div>© 2026 Groupardy. All rights reserved.</div>
            <div>Made with ❤️ for the group chat that always says "we should hang out more".</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
